import type { QueryClient } from "@tanstack/react-query";

import {
  getSettings,
  isPlatformSettings,
  type ValidatedPlatformSettings,
} from "./api";

export const PLATFORM_SETTINGS_QUERY_KEY = ["platform", "settings"] as const;
export const PLATFORM_SETTINGS_MUTATION_KEY = [
  "platform",
  "settings",
  "privileged-write",
] as const;

export class SettingsAuthorityError extends Error {
  constructor() {
    super("Platform settings could not be reconciled.");
    this.name = "SettingsAuthorityError";
  }
}

function validatedSettings(settings: unknown): ValidatedPlatformSettings {
  if (!isPlatformSettings(settings)) throw new SettingsAuthorityError();
  return settings;
}

async function forcePlatformSettingsRead(
  queryClient: QueryClient,
  operationIsCurrent: () => boolean = () => true,
): Promise<ValidatedPlatformSettings> {
  try {
    if (!operationIsCurrent()) throw new SettingsAuthorityError();
    await queryClient.cancelQueries({
      exact: true,
      queryKey: PLATFORM_SETTINGS_QUERY_KEY,
    });
    if (!operationIsCurrent()) throw new SettingsAuthorityError();
    await queryClient.invalidateQueries({
      exact: true,
      queryKey: PLATFORM_SETTINGS_QUERY_KEY,
      refetchType: "none",
    });
    if (!operationIsCurrent()) throw new SettingsAuthorityError();

    // Calling fetchQuery immediately after the lease check makes this a
    // distinct post-write request. QueryClient.clear() cancels it if the actor
    // changes while it is in flight.
    const settingsRead = queryClient.fetchQuery({
      queryKey: PLATFORM_SETTINGS_QUERY_KEY,
      queryFn: getSettings,
      retry: false,
      staleTime: 0,
    });
    const settings = await settingsRead;
    if (!operationIsCurrent()) throw new SettingsAuthorityError();
    return validatedSettings(settings);
  } catch {
    throw new SettingsAuthorityError();
  }
}

/** Reconcile a completed write with an observer-independent exact settings read. */
export async function reconcilePlatformSettings(
  queryClient: QueryClient,
  operationIsCurrent?: () => boolean,
): Promise<ValidatedPlatformSettings> {
  return forcePlatformSettingsRead(queryClient, operationIsCurrent);
}

/** Retry only the authoritative read; this never repeats the preceding write. */
export async function retryPlatformSettingsRead(
  queryClient: QueryClient,
): Promise<ValidatedPlatformSettings> {
  return forcePlatformSettingsRead(queryClient);
}
